import React, { useState, useEffect } from "react";
import { 
  X, 
  CheckCircle, 
  XCircle, 
  User, 
  Calendar, 
  Clock, 
  MessageSquare,
  AlertCircle,
  Check,
  Loader,
  ExternalLink,
  Link as LinkIcon,
  Maximize2,
  Minimize2
} from "lucide-react";

const TaskApplicationsView = ({
  task,
  onClose,
  onRespond,
  isCreator,
  selectedApplication,
  setSelectedApplication,
}) => {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Add ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [loading, onClose]);

  const handleApprove = async (applicationId) => {
    setLoading(true);
    try {
      await onRespond(applicationId, "approved", feedback);
      setSelectedApplication(null);
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error approving application:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (applicationId) => {
    setLoading(true);
    try {
      await onRespond(applicationId, "rejected", feedback);
      setSelectedApplication(null);
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error rejecting application:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle backdrop click to close the modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  // Status Badge component
  const StatusBadge = ({ status }) => {
    const statusClasses = {
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200"
    };
    
    return (
      <span className={`px-3 py-1 text-xs rounded-full font-medium border ${statusClasses[status] || statusClasses.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // If no application is selected, show a placeholder
  if (!selectedApplication) {
    return (
      <div className="border border-gray-200 rounded-md p-6 bg-white">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Application Details</h2>
          <button type="button" onClick={onClose} className="text-black hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No application selected</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`} 
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-xl shadow-2xl ${isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-3xl h-[85vh]'} flex flex-col animate-fadeIn`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl relative">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleFullscreen}
                className="mr-2 p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? 
                  <Minimize2 className="h-5 w-5" /> : 
                  <Maximize2 className="h-5 w-5" />
                }
              </button>
              <h2 className="text-2xl font-bold text-gray-800">Application Details</h2>
              <StatusBadge status={selectedApplication.status} />
            </div>
            <button 
              type="button"
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-100 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer z-50 shadow-sm"
              aria-label="Close"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className={`flex-grow overflow-y-auto p-6 custom-scrollbar ${isFullscreen ? 'h-[calc(100vh-14rem)]' : 'max-h-[70vh]'}`}>
          {/* Applicant Info */}
          <div className="bg-indigo-50/50 rounded-xl p-6 mb-8 border border-indigo-100 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-indigo-900 mb-2 flex items-center">
                  <User className="w-5 h-5 mr-2 text-indigo-600" />
                  Applicant
                </h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                  <div className="font-medium text-gray-900 mb-2">
                    <a 
                      href={`/innovators/${selectedApplication.contributorWalletAddress}`}
                      className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {selectedApplication.contributorWalletAddress?.slice(0, 10)}...
                    </a>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
                    <span>Applied {formatDate(selectedApplication.applicationDate)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-indigo-900 mb-2 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-indigo-600" />
                  Time Estimate
                </h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                  <div className="text-gray-800">
                    {selectedApplication.timeEstimate || "Not specified"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Proposal/Message */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
              Task Proposal
            </h3>
            <div className={`bg-white p-5 rounded-xl border border-gray-200 text-gray-800 shadow-sm min-h-24 overflow-y-auto custom-scrollbar ${isFullscreen ? 'max-h-96' : 'max-h-64'}`}>
              {selectedApplication.message ? (
                <p className="whitespace-pre-line">{selectedApplication.message}</p>
              ) : (
                <p className="text-gray-500 italic">No message provided</p>
              )}
            </div>
          </div>
          
          {/* Links section if provided */}
          {selectedApplication.links && selectedApplication.links.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <LinkIcon className="w-5 h-5 mr-2 text-indigo-600" />
                Relevant Links
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedApplication.links.map((link, index) => (
                  <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 flex items-center shadow-sm hover:shadow-md transition-shadow">
                    <ExternalLink className="w-5 h-5 mr-3 text-indigo-500 flex-shrink-0" />
                    <a 
                      href={link.startsWith('http') ? link : `https://${link}`} 
                      className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm break-all"
                    >
                      {link}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback section if application is already reviewed */}
          {selectedApplication.feedback && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                Feedback
              </h3>
              <div className="bg-white p-5 rounded-xl border border-gray-200 text-gray-700 shadow-sm">
                <p className="italic">"{selectedApplication.feedback}"</p>
              </div>
            </div>
          )}

          {/* Status info for approved/rejected applications */}
          {selectedApplication.status === "approved" && (
            <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Application has been approved</p>
                <p className="text-green-700 text-sm mt-1">
                  This applicant has been assigned to the task and can now submit work.
                </p>
              </div>
            </div>
          )}

          {selectedApplication.status === "rejected" && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Application has been rejected</p>
                <p className="text-red-700 text-sm mt-1">
                  This applicant was not selected for this task.
                </p>
              </div>
            </div>
          )}

          {/* Review feedback textarea for creator to add feedback */}
          {isCreator && selectedApplication.status === "pending" && (
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                Your Feedback (optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                rows="4"
                placeholder="Add feedback about this application..."
              />
            </div>
          )}

          {/* Warning when task is already in progress */}
          {isCreator && selectedApplication.status === "pending" && task.status !== "open" && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Task is no longer open</p>
                <p className="text-amber-700 text-sm mt-1">
                  This task is already in progress. You cannot approve new applications at this stage.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Fixed footer with action buttons */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {/* Review action buttons for creator when application is pending */}
          {isCreator && selectedApplication.status === "pending" && (
            <div className="flex justify-end space-x-4">
              {task.status === "open" ? (
                <>
                  <button
                    onClick={() => handleReject(selectedApplication._id)}
                    className="px-5 py-2.5 bg-white border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors shadow-sm"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="w-5 h-5 mr-2" />
                    )}
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedApplication._id)}
                    className="px-5 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors shadow-sm"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    Approve
                  </button>
                </>
              ) : (
                <div className="px-5 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Cannot approve - task is {task.status.replace('_', ' ')}
                </div>
              )}
            </div>
          )}
          
          {/* Show status in footer for already reviewed applications */}
          {selectedApplication.status !== "pending" && (
            <div className="flex justify-between items-center">
              <div className={`px-5 py-2.5 rounded-lg flex items-center ${
                selectedApplication.status === "approved" 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {selectedApplication.status === "approved" ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Application Approved
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 mr-2" />
                    Application Rejected
                  </>
                )}
              </div>
              
              <button
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add CSS for custom scrollbar */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TaskApplicationsView;
